import { useState } from 'react'
import { useCVGenerationStore } from '../../stores/cvGenerationStore'

export default function JDInputStep({ disabled: disabledProp = false }: { disabled?: boolean }) {
  const { startGeneration, loadState } = useCVGenerationStore()
  const loading = loadState === 'loading'
  const disabled = disabledProp || loading
  const [jdText, setJdText] = useState('')
  const [company, setCompany] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [notes, setNotes] = useState('')

  const canSubmit = jdText.trim().length > 0 && !disabled

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    await startGeneration(
      jdText.trim(),
      jobTitle.trim() || undefined,
      company.trim() || undefined,
      notes.trim() || undefined
    )
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-8">
      {/* Hero */}
      <div>
        <h1 className="text-2xl font-['IBM_Plex_Serif'] font-semibold text-[#1a1c1a] tracking-tight">
          Tailor your application
        </h1>
        <p className="text-sm text-[#545f6e] mt-2 leading-relaxed">
          Paste the job description below. We'll analyse it against your library,
          ask a few clarifying questions, and assemble a tailored CV.
        </p>
      </div>

      {/* JD Text Area */}
      <div>
        <label
          htmlFor="jd-text"
          className="block text-xs font-medium text-[#54433a] uppercase tracking-wider mb-2"
        >
          Job Description
        </label>
        <textarea
          id="jd-text"
          value={jdText}
          onChange={(e) => setJdText(e.target.value)}
          placeholder="Paste the full job description here..."
          rows={12}
          disabled={disabled}
          className="w-full bg-white border border-[#dac2b6]/30 rounded-lg px-4 py-3 text-sm text-[#1a1c1a] placeholder:text-[#877369] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]/20 resize-y transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <p className="text-xs text-[#877369] mt-1 text-right">
          {jdText.length.toLocaleString()} characters
        </p>
      </div>

      {/* Optional fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="company"
            className="block text-xs font-medium text-[#54433a] uppercase tracking-wider mb-2"
          >
            Company name <span className="text-[#877369] normal-case">(optional)</span>
          </label>
          <input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g. Acme Corp"
            disabled={disabled}
            className="w-full bg-white border border-[#dac2b6]/30 rounded-lg px-4 py-2.5 text-sm text-[#1a1c1a] placeholder:text-[#877369] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label
            htmlFor="job-title"
            className="block text-xs font-medium text-[#54433a] uppercase tracking-wider mb-2"
          >
            Role title <span className="text-[#877369] normal-case">(optional)</span>
          </label>
          <input
            id="job-title"
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Senior Product Manager"
            disabled={disabled}
            className="w-full bg-white border border-[#dac2b6]/30 rounded-lg px-4 py-2.5 text-sm text-[#1a1c1a] placeholder:text-[#877369] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label
          htmlFor="notes"
          className="block text-xs font-medium text-[#54433a] uppercase tracking-wider mb-2"
        >
          Notes for this application{' '}
          <span className="text-[#877369] normal-case">(optional — not shown in CV)</span>
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything you'd like to highlight or avoid..."
          rows={3}
          disabled={disabled}
          className="w-full bg-white border border-[#dac2b6]/30 rounded-lg px-4 py-3 text-sm text-[#1a1c1a] placeholder:text-[#877369] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]/20 resize-y transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={!canSubmit || disabled}
          className="px-6 py-3 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              Analysing...
            </span>
          ) : (
            'Analyse Job Description →'
          )}
        </button>
      </div>
    </form>
  )
}
